// daoud brand — upcoming shows list. Primary path: the list arrives already
// server-rendered from a cached Bandsintown snapshot (see daoud-shows.liquid),
// so this only adds locale-correct dates, scroll-reveal, and nearest-show
// promotion on top of markup that's already real and crawlable. Fallback path
// (initFromFetch): if that cache is ever empty, fetches Bandsintown's public
// API directly client-side, same as this file's original client-only design.
(function () {
  function readStrings(root) {
    return {
      getTickets: root.querySelector('[data-str-get-tickets]').textContent,
      soldOut: root.querySelector('[data-str-sold-out]').textContent,
      moreInfo: root.querySelector('[data-str-more-info]').textContent,
      nearYou: root.querySelector('[data-str-near-you]').textContent,
    };
  }

  function formatDate(isoString) {
    var date = new Date(isoString);
    var lang = document.documentElement.lang || 'en';
    var day = new Intl.DateTimeFormat(lang, { day: 'numeric' }).format(date);
    var month = new Intl.DateTimeFormat(lang, { month: 'short' }).format(date).replace('.', '');
    var year = new Intl.DateTimeFormat(lang, { year: 'numeric' }).format(date);
    return { day: day, monthYear: month + ' ' + year };
  }

  // Diagnostic logging for the geolocation path only — visible in the browser
  // console as "[daoud-shows]"-prefixed lines, harmless in production, and the
  // fastest way to see exactly where "near you" fails for a given visitor
  // (network error vs timeout vs blocked request vs bad response shape).
  function log() {
    if (window.console && console.log) {
      console.log.apply(console, ['[daoud-shows]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function fetchJson(url, timeoutMs) {
    var controller = new AbortController();
    var timeout = setTimeout(function () {
      log('timed out after', timeoutMs, 'ms:', url);
      controller.abort();
    }, timeoutMs);

    return fetch(url, { signal: controller.signal })
      .then(function (response) {
        log('response', response.status, 'from', url);
        return response.json();
      })
      .catch(function (err) {
        log('request failed:', url, err && err.message);
        return null;
      })
      .finally(function () {
        clearTimeout(timeout);
      });
  }

  function getUserCoords() {
    // IP-based lookup instead of the browser Geolocation API: no permission
    // prompt, city-level accuracy (good enough for sorting a short list of
    // shows), degrades silently to chronological order on any failure. Runs
    // independently of the shows list render (see init()) rather than
    // blocking it, since mobile/cellular networks can take much longer than
    // wifi to resolve a third-party request.
    //
    // Two providers, requested in parallel rather than chained: mobile
    // carriers route through carrier-grade NAT, and a single free geolocation
    // API can occasionally mis-flag or fail on that traffic even when it
    // resolves fine from wifi. Chaining them (try A, only then try B) stacks
    // their timeouts — a visitor on a slow connection could get cut off from
    // BOTH in sequence before either genuinely finishes. Firing both at once
    // with a generous per-request timeout means a slow-but-working request
    // still gets a fair chance, and the total worst-case wait doesn't grow
    // just because the first provider happened to be slow.
    log('resolving visitor location…');
    var primary = fetchJson('https://ipwho.is/', 8000).then(function (data) {
      if (data && data.success && isFinite(data.latitude) && isFinite(data.longitude)) {
        log('resolved via ipwho.is:', data.city, data.country, data.latitude, data.longitude);
        return { lat: data.latitude, lng: data.longitude };
      }
      log('ipwho.is gave no usable result', data);
      return null;
    });
    var fallback = fetchJson('https://get.geojs.io/v1/ip/geo.json', 8000).then(function (data) {
      var lat = data ? parseFloat(data.latitude) : NaN;
      var lng = data ? parseFloat(data.longitude) : NaN;
      if (!isFinite(lat) || !isFinite(lng)) {
        log('geojs.io gave no usable result', data);
        return null;
      }
      log('resolved via geojs.io:', data.city, data.country, lat, lng);
      return { lat: lat, lng: lng };
    });
    return Promise.all([primary, fallback]).then(function (results) {
      var coords = results[0] || results[1];
      if (!coords) log('both providers failed — list stays chronological');
      return coords;
    });
  }

  function distanceKm(lat1, lng1, lat2, lng2) {
    var toRad = function (deg) {
      return (deg * Math.PI) / 180;
    };
    var earthRadiusKm = 6371;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function findNearestIndex(events, userCoords) {
    var bestIndex = -1;
    var bestDistance = Infinity;
    events.forEach(function (event, index) {
      var venue = event.venue || {};
      var lat = parseFloat(venue.latitude);
      var lng = parseFloat(venue.longitude);
      if (!isFinite(lat) || !isFinite(lng)) return;
      var distance = distanceKm(userCoords.lat, userCoords.lng, lat, lng);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  }

  // Same nearest-match logic as findNearestIndex, but reads lat/lng straight off
  // already-rendered SSR rows (data-lat/data-lng) instead of a fetched events array.
  function findNearestRowIndex(rows, userCoords) {
    var bestIndex = -1;
    var bestDistance = Infinity;
    rows.forEach(function (row, index) {
      var lat = parseFloat(row.dataset.lat);
      var lng = parseFloat(row.dataset.lng);
      if (!isFinite(lat) || !isFinite(lng)) return;
      var distance = distanceKm(userCoords.lat, userCoords.lng, lat, lng);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    return bestIndex;
  }

  function isSafeUrl(url) {
    if (typeof url !== 'string') return false;
    try {
      var parsed = new URL(url, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  function locationText(venue) {
    var parts = [venue.city, venue.region, venue.country].filter(function (part) {
      return part && part.length;
    });
    // Avoid "Paris, Île-de-France, France" — city + country reads cleanest;
    // only fall back to region when country is missing (rare in the API).
    if (venue.city && venue.country) return venue.city + ', ' + venue.country;
    return parts.join(', ');
  }

  function buildRow(event, index, strings) {
    var li = document.createElement('li');
    li.className = 'daoud-shows__row reveal-pending';
    li.style.setProperty('--reveal-delay', Math.min(index, 10) * 40 + 'ms');

    var when = formatDate(event.datetime);
    var dateWrap = document.createElement('div');
    dateWrap.className = 'daoud-shows__date';
    var dayEl = document.createElement('span');
    dayEl.className = 'daoud-shows__date-day';
    dayEl.textContent = when.day;
    var monthEl = document.createElement('span');
    monthEl.className = 'daoud-shows__date-month';
    monthEl.textContent = when.monthYear;
    dateWrap.appendChild(dayEl);
    dateWrap.appendChild(monthEl);
    li.appendChild(dateWrap);

    var details = document.createElement('div');
    details.className = 'daoud-shows__details';
    var venueEl = document.createElement('p');
    venueEl.className = 'daoud-shows__venue';
    venueEl.textContent = event.venue && event.venue.name ? event.venue.name : '';
    var locationEl = document.createElement('p');
    locationEl.className = 'daoud-shows__location';
    locationEl.textContent = event.venue ? locationText(event.venue) : '';
    details.appendChild(venueEl);
    details.appendChild(locationEl);
    li.appendChild(details);

    var action = document.createElement('div');
    action.className = 'daoud-shows__action';
    if (event.sold_out) {
      var badge = document.createElement('span');
      badge.className = 'daoud-shows__sold-out';
      badge.textContent = strings.soldOut;
      action.appendChild(badge);
    } else {
      var offer = (event.offers || []).find(function (o) {
        return o.status === 'available' && o.url;
      });
      var ticketUrl = offer ? offer.url : event.url;
      // ticketUrl comes straight from Bandsintown's API — validate the scheme
      // before using it as a link href so a compromised/malformed response
      // can't produce a javascript:/data: URL.
      if (isSafeUrl(ticketUrl)) {
        var link = document.createElement('a');
        link.className = 'button button-secondary';
        link.href = ticketUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = offer ? strings.getTickets : strings.moreInfo;
        action.appendChild(link);
      }
    }
    li.appendChild(action);

    return li;
  }

  function revealOnScroll(rows) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.1 }
    );
    rows.forEach(function (row) {
      observer.observe(row);
    });
  }

  // Pins the nearest show to the top of the list as a second, visually distinct
  // copy — the original row stays untouched in its normal chronological spot
  // below (an earlier version moved/bannered instead of duplicating; both left
  // the visitor unsure whether the show still "lived" at its real date, or
  // required a click to discover it existed). Cloning keeps both readable at a
  // glance: the pin says "this is the one nearest you", the row at its real
  // date confirms exactly when in the full schedule that is.
  function promoteNearest(listEl, row, strings) {
    var pin = row.cloneNode(true);
    pin.classList.remove('reveal-pending');
    pin.classList.add('is-revealed', 'daoud-shows__row--pinned');
    pin.style.removeProperty('--reveal-delay');

    var label = document.createElement('span');
    label.className = 'daoud-shows__near-label';
    label.textContent = strings.nearYou;
    pin.insertBefore(label, pin.firstChild);

    listEl.insertBefore(pin, listEl.firstChild);

    var divider = document.createElement('li');
    divider.className = 'daoud-shows__divider';
    divider.setAttribute('aria-hidden', 'true');
    listEl.insertBefore(divider, pin.nextSibling);
  }

  // Rows arrive server-rendered with a locale-neutral date (day number + English
  // abbreviated month, so search engines and no-JS visitors still get real text).
  // This swaps that in for the visitor's actual locale once JS runs, reusing the
  // exact same formatDate() the fetch-fallback path uses, so both paths render
  // dates identically for real visitors.
  function reformatSsrDates(rows) {
    rows.forEach(function (row) {
      var timeEl = row.querySelector('time[datetime]');
      if (!timeEl) return;
      var when = formatDate(timeEl.getAttribute('datetime'));
      var dayEl = row.querySelector('.daoud-shows__date-day');
      var monthEl = row.querySelector('.daoud-shows__date-month');
      if (dayEl) dayEl.textContent = when.day;
      if (monthEl) monthEl.textContent = when.monthYear;
    });
  }

  // Primary path: the list is already real, server-rendered HTML from the cached
  // Bandsintown snapshot (see daoud-shows.liquid) — no fetch needed. JS only adds
  // locale-correct date text, scroll-reveal, and nearest-show promotion on top of
  // markup that's already complete and crawlable.
  function initFromSsr(root, listEl, strings) {
    var rows = Array.prototype.slice.call(listEl.querySelectorAll('.daoud-shows__row'));
    reformatSsrDates(rows);
    revealOnScroll(rows);

    getUserCoords().then(function (userCoords) {
      if (!userCoords) {
        log('no coordinates resolved — list stays chronological, no "near you" badge');
        return;
      }
      var nearestIndex = findNearestRowIndex(rows, userCoords);
      if (nearestIndex === -1) {
        log('coordinates resolved but no row had usable venue lat/lng to compare against');
        return;
      }
      log('nearest row is index', nearestIndex, '— promoting');
      promoteNearest(listEl, rows[nearestIndex], strings);
    });
  }

  // Fallback path: used only if the server-side cache is empty (e.g. before the
  // first background sync has ever run) so the page never shows a broken empty
  // state. Fetches Bandsintown directly, same as the original client-only version.
  function initFromFetch(root, listEl, emptyEl, errorEl, strings) {
    var handle = root.dataset.artistHandle;
    var appId = root.dataset.appId;

    function showError() {
      listEl.hidden = true;
      errorEl.hidden = false;
    }

    if (!handle || !appId) {
      showError();
      return;
    }

    var endpoint =
      'https://rest.bandsintown.com/artists/' +
      encodeURIComponent(handle) +
      '/events/?app_id=' +
      encodeURIComponent(appId) +
      '&date=upcoming';

    fetch(endpoint)
      .then(function (response) {
        if (!response.ok) throw new Error('Bandsintown request failed: ' + response.status);
        return response.json();
      })
      .then(function (events) {
        if (!Array.isArray(events) || events.length === 0) {
          listEl.hidden = true;
          emptyEl.hidden = false;
          return;
        }

        listEl.innerHTML = '';
        listEl.classList.remove('daoud-shows__list--loading');
        listEl.classList.add('daoud-shows__list--loaded');

        var rows = events.map(function (event, index) {
          var row = buildRow(event, index, strings);
          listEl.appendChild(row);
          return row;
        });

        revealOnScroll(rows);

        // Geolocation runs independently and can arrive well after the list
        // itself — the pin is a progressive enhancement, not something the
        // list should wait on.
        getUserCoords().then(function (userCoords) {
          if (!userCoords) return;
          var nearestIndex = findNearestIndex(events, userCoords);
          if (nearestIndex === -1) return;
          promoteNearest(listEl, rows[nearestIndex], strings);
        });
      })
      .catch(function () {
        showError();
      });
  }

  function init(root) {
    var listEl = root.querySelector('[data-shows-list]');
    var emptyEl = root.querySelector('[data-shows-empty]');
    var errorEl = root.querySelector('[data-shows-error]');
    var strings = readStrings(root);

    if (listEl.dataset.ssr === 'true') {
      initFromSsr(root, listEl, strings);
      return;
    }

    initFromFetch(root, listEl, emptyEl, errorEl, strings);
  }

  function bind() {
    document.querySelectorAll('[data-daoud-shows]:not([data-bound])').forEach(function (root) {
      root.dataset.bound = 'true';
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);
})();
